import React, { useState } from 'react';
import { useStore, getActivePage } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';
import { useI18n } from '@/i18n/I18nContext';
import { DEVICE_TYPES, deviceLabel, isPassiveDevice } from '@/lib/deviceIcons';
import { CONNECTION_TYPES, connLabel } from '@/lib/connections';
import { DeviceIcon } from '@/components/DeviceIcon';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ipInSubnet, subnetInfo, ipFixedPrefix } from '@/lib/subnet';
import { HostIpInput } from '@/components/editor/HostIpInput';
import { findDeviceGlobal, findEdgeGlobal } from '@/lib/detail';
import { getPanelGroups, PANEL_LAYOUTS } from '@/lib/panel';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, CheckCircle2, AlertCircle, MousePointer2, Cable, Plus, GripVertical, LayoutGrid, ChevronDown, Tags, Check, X, Network, Copy } from 'lucide-react';

const Header = ({ title }) => (
  <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
  </div>
);

// Per-device front-panel layout configuration: prefix groups can be reordered
// (drag & drop, left -> right) and each group gets one of 4 layouts. Persisted
// on the device so it applies across all detail pages and survives draw.io round-trip.
function PanelLayoutSection({ groups, onChange, t }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const persist = (arr) => onChange({ groups: arr.map((g) => ({ prefix: g.prefix, layout: g.layout || 'single' })) });

  const setLayout = (prefix, layout) =>
    persist(groups.map((g) => ({ prefix: g.prefix, layout: g.prefix === prefix ? layout : g.layout || 'single' })));

  const handleDrop = (to) => {
    const from = dragIdx;
    setDragIdx(null);
    setOverIdx(null);
    if (from == null || from === to) return;
    const arr = groups.map((g) => ({ prefix: g.prefix, layout: g.layout || 'single' }));
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    persist(arr);
  };

  return (
    <Collapsible defaultOpen className="rounded-md border border-border bg-white" data-testid="panel-layout-section">
      <CollapsibleTrigger className="group flex w-full items-center justify-between px-3 py-2.5 text-left" data-testid="panel-layout-toggle">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <LayoutGrid className="h-3.5 w-3.5" /> {t('panel_layout')}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-150 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1.5 px-3 pb-3">
        <p className="text-[11px] leading-snug text-muted-foreground">{t('panel_layout_hint')}</p>
        {groups.map((g, i) => (
          <div
            key={g.prefix}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIdx(i);
            }}
            onDrop={() => handleDrop(i)}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            className={`flex items-center gap-1.5 rounded-md border bg-[#FBFBFA] p-1.5 transition-colors duration-150 ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? 'border-[#0B5FFF] bg-[#0B5FFF]/5' : 'border-border'
            } ${dragIdx === i ? 'opacity-50' : ''}`}
            data-testid={`panel-group-row-${g.prefix || 'none'}`}
          >
            <span
              className="flex h-7 w-5 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing"
              aria-hidden="true"
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-[13px] font-semibold text-[#111418]">{g.prefix || '\u2014'}</div>
              <div className="text-[10px] text-muted-foreground">{t('panel_group_ports', { n: g.ports.length })}</div>
            </div>
            <Select value={g.layout || 'single'} onValueChange={(v) => setLayout(g.prefix, v)}>
              <SelectTrigger className="h-8 w-[156px]" data-testid={`panel-layout-select-${g.prefix || 'none'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PANEL_LAYOUTS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {t(l.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Per-port VLAN configuration: one untagged/access VLAN + a list of tagged
// (trunk) VLANs. Independent from the device's management VLAN (vlanRef).
function PortVlanPopover({ node, port, vlans, t, applyPort, withIp = false }) {
  const untagged = port.untaggedVlanRef || null;
  const tagged = Array.isArray(port.taggedVlanRefs) ? port.taggedVlanRefs : [];
  const uVlan = vlans.find((v) => v.id === untagged) || null;
  const hasIp = withIp && !!(port.ip || '').trim();

  const setUntagged = (v) => applyPort(port.id, { untaggedVlanRef: v === 'none' ? null : v });
  const toggleTagged = (id) => {
    const set = new Set(tagged);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    applyPort(port.id, { taggedVlanRefs: Array.from(set) });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1 px-1.5"
          title={t('port_vlan_config')}
          aria-label={t('port_vlan_config')}
          data-testid={`port-vlan-trigger-${port.id}`}
        >
          <span
            className="h-3 w-3 rounded-full border"
            style={{ background: uVlan ? uVlan.color : 'transparent', borderColor: uVlan ? uVlan.color : '#cbd5e1' }}
          />
          {tagged.length > 0 ? (
            <span className="text-[10px] font-semibold tabular-nums text-[#0B5FFF]" data-testid={`port-tagged-count-${port.id}`}>+{tagged.length}</span>
          ) : (
            <Tags className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {hasIp && <span className="h-1.5 w-1.5 rounded-full bg-[#0F7B6C]" title={port.ip} data-testid={`port-ip-dot-${port.id}`} />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid={`port-vlan-popover-${port.id}`}>
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <Tags className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('port_vlans')}</span>
            <span className="ml-auto font-mono text-[11px] text-[#111418]">{port.name}</span>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('port_untagged')}</Label>
            <Select value={untagged || 'none'} onValueChange={setUntagged}>
              <SelectTrigger className="h-8" data-testid={`port-untagged-select-${port.id}`}>
                <SelectValue placeholder={t('port_no_untagged')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('port_no_untagged')}</SelectItem>
                {vlans.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                      VLAN {v.vlanId} · {v.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('port_tagged')}</Label>
            {vlans.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">{t('no_vlans_title')}</p>
            ) : (
              <div className="max-h-44 space-y-0.5 overflow-auto rounded-md border border-border bg-white p-1">
                {vlans.map((v) => {
                  const on = tagged.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleTagged(v.id)}
                      data-testid={`port-tagged-toggle-${port.id}-${v.id}`}
                      aria-pressed={on}
                      className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors duration-150 ${
                        on ? 'bg-[#0B5FFF]/10 text-[#111418]' : 'hover:bg-muted text-[#2A3138]'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-[#0B5FFF] bg-[#0B5FFF] text-white' : 'border-input bg-white'}`}
                      >
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: v.color }} />
                      <span className="truncate">VLAN {v.vlanId} · {v.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {withIp && (
            <div className="space-y-1.5 border-t border-border pt-2.5">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('port_ip')}</Label>
              <HostIpInput
                cidr={uVlan?.network}
                value={port.ip || ''}
                onChange={(val) => applyPort(port.id, { ip: val })}
                placeholder={uVlan ? t('ip_host_placeholder') : t('ip_placeholder')}
                testid={`port-ip-input-${port.id}`}
              />
            </div>
          )}

          <p className="text-[10.5px] leading-snug text-muted-foreground">{t('port_vlan_hint')}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConnSample({ c }) {
  return (
    <svg width="30" height="10" className="shrink-0">
      <line x1="1" y1="5" x2="29" y2="5" stroke={c.color} strokeWidth={c.width} strokeDasharray={c.dash || undefined} />
    </svg>
  );
}

// Endpoint side selector (auto / top / right / bottom / left) — lets a user fix
// which side of the device a connection attaches to.
function SideSelect({ value, onChange, testid, language }) {
  const opts =
    language === 'en'
      ? [['auto', 'Auto'], ['t', 'Top'], ['r', 'Right'], ['b', 'Bottom'], ['l', 'Left']]
      : [['auto', 'Auto'], ['t', 'Oben'], ['r', 'Rechts'], ['b', 'Unten'], ['l', 'Links']];
  return (
    <Select value={value || 'auto'} onValueChange={(v) => onChange(v === 'auto' ? null : v)}>
      <SelectTrigger className="h-8 text-xs" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {opts.map(([v, l]) => (
          <SelectItem key={v} value={v} className="text-xs">
            {l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// LAG grouping for the selected connection: bundle parallel edges between the
// same two devices (similar to port groups).
function LagSection({ edge, page, language, t }) {
  const createLag = useStore((s) => s.createLag);
  const addEdgesToLag = useStore((s) => s.addEdgesToLag);
  const removeEdgeFromLag = useStore((s) => s.removeEdgeFromLag);
  const deleteLag = useStore((s) => s.deleteLag);
  const updateLag = useStore((s) => s.updateLag);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState([]);
  const [targetLag, setTargetLag] = useState('');

  const pairKey = (a, b) => [a, b].sort().join('|');
  const key = pairKey(edge.source, edge.target);
  const siblings = (page?.edges || []).filter((e) => e.id !== edge.id && pairKey(e.source, e.target) === key);
  const lags = page?.lags || [];
  const lag = edge.lagId ? lags.find((l) => l.id === edge.lagId) : null;
  const portName = (nodeId, portId) => {
    const n = page?.nodes.find((x) => x.id === nodeId);
    return n?.data?.ports?.find((p) => p.id === portId)?.name || '—';
  };
  const edgeDesc = (e) => `${portName(e.source, e.sourcePortId)} \u2194 ${portName(e.target, e.targetPortId)}`;

  if (lag) {
    return (
      <div className="space-y-2 rounded-md border border-[#0B5FFF]/30 bg-[#0B5FFF]/5 p-3" data-testid="lag-section">
        <Label className="text-xs uppercase tracking-wide text-[#0B5FFF]">{t('lag_member')}</Label>
        <Input value={lag.name} onChange={(e) => updateLag(lag.id, { name: e.target.value })} className="h-8 text-sm" data-testid="lag-name-input" />
        <div className="text-[11px] text-muted-foreground">{(lag.memberEdgeIds || []).length} {t('lag_links')}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" onClick={() => removeEdgeFromLag(edge.id)} data-testid="lag-remove-edge">
            {t('lag_remove_this')}
          </Button>
          <Button variant="outline" size="sm" className="h-7 flex-1 border-[#B42318]/30 text-xs text-[#B42318]" onClick={() => deleteLag(lag.id)} data-testid="lag-dissolve">
            {t('lag_dissolve')}
          </Button>
        </div>
      </div>
    );
  }

  if (!siblings.length) return null;
  const freeSiblings = siblings.filter((e) => !e.lagId);
  const laggedSiblings = siblings.filter((e) => e.lagId);
  const pairLagIds = Array.from(new Set(laggedSiblings.map((e) => e.lagId)));
  const pairLags = pairLagIds.map((id) => lags.find((l) => l.id === id)).filter(Boolean);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="space-y-2.5 rounded-md border border-border bg-white p-3" data-testid="lag-create-section">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('lag_group_title')}</Label>
      <p className="text-[11px] text-muted-foreground">{t('lag_group_hint')}</p>

      <div className="max-h-32 space-y-1 overflow-auto">
        {freeSiblings.map((e) => (
          <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-muted/60">
            <Checkbox checked={picked.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`lag-pick-${e.id}`} />
            <span className="truncate font-mono">{edgeDesc(e)}</span>
          </label>
        ))}
        {laggedSiblings.map((e) => {
          const l = lags.find((x) => x.id === e.lagId);
          return (
            <div key={e.id} className="flex items-center gap-2 rounded px-1 py-0.5 text-xs opacity-70" data-testid={`lag-locked-${e.id}`}>
              <span className="truncate font-mono">{edgeDesc(e)}</span>
              <span className="ml-auto shrink-0 whitespace-nowrap rounded-full bg-[#0B5FFF]/10 px-1.5 py-[1px] text-[9px] font-semibold text-[#0B5FFF]">
                {t('lag_in_lag', { name: l?.name || 'LAG' })}
              </span>
            </div>
          );
        })}
        {!freeSiblings.length && <p className="px-1 text-[11px] text-muted-foreground">{t('lag_no_free')}</p>}
      </div>

      {/* Create a NEW LAG — always available, so multiple independent LAGs
          between the same device pair are possible. */}
      <div className="space-y-1.5 border-t border-border pt-2.5">
        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('lag_create_new')}</Label>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('lag_name_placeholder')} className="h-7 text-xs" data-testid="lag-new-name" />
          <Button
            size="sm"
            className="h-7 whitespace-nowrap text-xs"
            disabled={!freeSiblings.length || !picked.length}
            onClick={() => {
              createLag([edge.id, ...picked], name || 'LAG');
              setName('');
              setPicked([]);
            }}
            data-testid="lag-create-button"
          >
            {t('lag_create')}
          </Button>
        </div>
      </div>

      {/* Add the current connection (+ any picked free links) to an existing
          LAG between this pair. */}
      {pairLags.length > 0 && (
        <div className="space-y-1.5 border-t border-border pt-2.5">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('lag_add_to_section')}</Label>
          <div className="flex gap-2">
            <Select value={targetLag} onValueChange={setTargetLag}>
              <SelectTrigger className="h-7 flex-1 text-xs" data-testid="lag-existing-select">
                <SelectValue placeholder={t('lag_select_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {pairLags.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-7 whitespace-nowrap text-xs"
              disabled={!targetLag}
              onClick={() => {
                addEdgesToLag(targetLag, [edge.id, ...picked]);
                setPicked([]);
                setTargetLag('');
              }}
              data-testid="lag-add-to-existing"
            >
              {t('lag_add_button')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Inspector = () => {
  const { t, language } = useI18n();
  const page = useStore(getActivePage);
  const doc = useStore((s) => s.doc);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const vlans = useStore((s) => s.doc.vlans);
  const updateNodeData = useStore((s) => s.updateNodeData);
  const updateDeviceGlobal = useStore((s) => s.updateDeviceGlobal);
  const deleteNode = useStore((s) => s.deleteNode);
  const removeDetailMember = useStore((s) => s.removeDetailMember);
  const removeLocationMarker = useStore((s) => s.removeLocationMarker);
  const updateEdge = useStore((s) => s.updateEdge);
  const deleteEdge = useStore((s) => s.deleteEdge);
  const openConnDialog = useStore((s) => s.openConnDialog);
  const addPorts = useStore((s) => s.addPorts);
  const updatePort = useStore((s) => s.updatePort);
  const deletePort = useStore((s) => s.deletePort);
  const createPortGroup = useStore((s) => s.createPortGroup);
  const updatePortGroup = useStore((s) => s.updatePortGroup);
  const deletePortGroup = useStore((s) => s.deletePortGroup);
  const addPortsToGroup = useStore((s) => s.addPortsToGroup);
  const removePortFromGroup = useStore((s) => s.removePortFromGroup);
  const addInterface = useStore((s) => s.addInterface);
  const updateInterface = useStore((s) => s.updateInterface);
  const deleteInterface = useStore((s) => s.deleteInterface);

  const [portPrefix, setPortPrefix] = useState('eth');
  const [portCount, setPortCount] = useState(1);
  const [selectedPortIds, setSelectedPortIds] = useState([]);
  React.useEffect(() => {
    setSelectedPortIds([]);
  }, [selectedNodeId]);

  const isDetail = page?.type === 'detail';
  const isLocation = page?.type === 'location';
  // The raw placement node on the active standard page — used to detect an alias
  // (clone) placement and as the delete target (removing just this placement).
  const rawNode = isDetail || isLocation ? null : page?.nodes?.find((n) => n.id === selectedNodeId) || null;
  const isAliasPlacement = !!rawNode?.data?.aliasOf;
  // Editing always targets the MASTER device (single source of truth). Detail/
  // location pages already resolve globally; an alias on a standard page does too.
  const isGlobal = isDetail || isLocation || isAliasPlacement;
  const node = isGlobal
    ? findDeviceGlobal(doc, selectedNodeId)
    : rawNode;
  // Resolve the selected connection globally so it can be edited from a detail
  // page too (the edge lives on the standard page that owns its devices).
  const edgeHit = selectedEdgeId ? findEdgeGlobal(doc, selectedEdgeId) : null;
  const edge = edgeHit?.edge || null;
  const edgePage = edgeHit?.page || page;
  const applyData = (patch) => (isGlobal ? updateDeviceGlobal(node.id, patch) : updateNodeData(node.id, patch));

  const nameOf = (n) => (n ? n.data.label || deviceLabel(n.data.deviceType, language) : '—');

  // ---- Edge (connection) inspector ----
  if (edge) {
    const src = findDeviceGlobal(doc, edge.source);
    const tgt = findDeviceGlobal(doc, edge.target);
    const srcPorts = src?.data.ports || [];
    const tgtPorts = tgt?.data.ports || [];

    // Resolve a port's name on any device (across all pages).
    const portNameOf = (devId, portId) => {
      const n = findDeviceGlobal(doc, devId);
      return n?.data?.ports?.find((p) => p.id === portId)?.name || '\u2014';
    };
    // For a device, map each already-connected port -> the peer device + port,
    // so the dropdown can show what an occupied port is wired to. The currently
    // edited connection is excluded so its own port is not flagged as "in use".
    const buildPortUsage = (devId) => {
      const map = {};
      (doc.pages || []).forEach((pg) => {
        (pg.edges || []).forEach((e) => {
          if (e.id === edge.id) return;
          if (e.source === devId && e.sourcePortId && !map[e.sourcePortId]) {
            map[e.sourcePortId] = { peerName: nameOf(findDeviceGlobal(doc, e.target)), peerPort: portNameOf(e.target, e.targetPortId) };
          } else if (e.target === devId && e.targetPortId && !map[e.targetPortId]) {
            map[e.targetPortId] = { peerName: nameOf(findDeviceGlobal(doc, e.source)), peerPort: portNameOf(e.source, e.sourcePortId) };
          }
        });
      });
      return map;
    };
    const srcUsage = buildPortUsage(edge.source);
    const tgtUsage = buildPortUsage(edge.target);

    const PortSelect = ({ ports, dev, value, onChange, testid, usage }) => {
      if (!ports.length) {
        return <p className="text-xs text-[#B7791F]">{t('no_ports_on_device', { name: nameOf(dev) })}</p>;
      }
      return (
        <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? null : v)}>
          <SelectTrigger className="h-9" data-testid={testid}>
            <SelectValue placeholder={t('no_port')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('no_port')}</SelectItem>
            {ports.map((p) => {
              const u = usage && usage[p.id];
              return (
                <SelectItem key={p.id} value={p.id} data-testid={`${testid}-opt-${p.id}`}>
                  <span className="flex w-full items-center gap-2">
                    <span className="shrink-0 font-mono">{p.name}</span>
                    {u && (
                      <span
                        className="ml-auto flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground"
                        title={`${u.peerName} \u00b7 ${u.peerPort}`}
                      >
                        <Cable className="h-3 w-3 shrink-0 opacity-70" />
                        <span className="truncate">{u.peerName}</span>
                        <span className="shrink-0 font-mono opacity-70">{`\u00b7 ${u.peerPort}`}</span>
                      </span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    };

    return (
      <div className="flex h-full flex-col bg-[#FBFBFA]" data-testid="inspector-panel">
        <Header title={t('connection_selected')} />
        <div className="min-h-0 flex-1 space-y-5 overflow-auto px-4 py-4">
          <div className="flex items-center gap-3 rounded-md border border-border bg-white p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded bg-muted text-[#2A3138]">
              <Cable className="h-4 w-4" />
            </div>
            <div className="min-w-0 text-sm">
              <div className="truncate"><span className="text-muted-foreground">{t('from_device')}:</span> <span className="font-semibold text-[#111418]" data-testid="inspector-edge-source-device">{nameOf(src)}</span></div>
              <div className="truncate"><span className="text-muted-foreground">{t('to_device')}:</span> <span className="font-semibold text-[#111418]" data-testid="inspector-edge-target-device">{nameOf(tgt)}</span></div>
            </div>
          </div>

          {srcPorts.length > 0 && tgtPorts.length > 0 && (
            <Button
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => openConnDialog(edge.id)}
              data-testid="inspector-open-conn-dialog"
            >
              <Cable className="h-4 w-4" /> {t('conn_configure')}
            </Button>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('connection_label')}</Label>
            <Input
              value={edge.name || ''}
              onChange={(e) => updateEdge(edge.id, { name: e.target.value })}
              placeholder={t('connection_label_placeholder')}
              className="h-9"
              data-testid="inspector-connection-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('connection_type')}</Label>
            <Select value={edge.connType || 'ethernet'} onValueChange={(v) => updateEdge(edge.id, { connType: v })}>
              <SelectTrigger className="h-9" data-testid="inspector-conn-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONNECTION_TYPES.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    <span className="inline-flex items-center gap-2">
                      <ConnSample c={c} /> {connLabel(c.key, language)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
              <span className="uppercase tracking-wide text-muted-foreground">{t('source_port')}</span>
              <span className="font-semibold text-[#111418]" data-testid="inspector-source-device-name">{nameOf(src)}</span>
            </Label>
            <PortSelect
              ports={srcPorts}
              dev={src}
              value={edge.sourcePortId}
              onChange={(v) => updateEdge(edge.id, { sourcePortId: v })}
              testid="inspector-source-port-select"
              usage={srcUsage}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
              <span className="uppercase tracking-wide text-muted-foreground">{t('target_port')}</span>
              <span className="font-semibold text-[#111418]" data-testid="inspector-target-device-name">{nameOf(tgt)}</span>
            </Label>
            <PortSelect
              ports={tgtPorts}
              dev={tgt}
              value={edge.targetPortId}
              onChange={(v) => updateEdge(edge.id, { targetPortId: v })}
              testid="inspector-target-port-select"
              usage={tgtUsage}
            />
          </div>

          <div className="space-y-2 rounded-md border border-border bg-white p-3">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('endpoint_sides')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="text-[11px] text-muted-foreground">{t('source_side')}</span>
                <SideSelect value={edge.sourceSide} onChange={(v) => updateEdge(edge.id, { sourceSide: v })} testid="inspector-source-side-select" language={language} />
              </div>
              <div className="space-y-1">
                <span className="text-[11px] text-muted-foreground">{t('target_side')}</span>
                <SideSelect value={edge.targetSide} onChange={(v) => updateEdge(edge.id, { targetSide: v })} testid="inspector-target-side-select" language={language} />
              </div>
            </div>
          </div>

          <LagSection edge={edge} page={edgePage} language={language} t={t} nameOf={nameOf} />

          <Button
            variant="outline"
            className="w-full border-[#B42318]/30 text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
            onClick={() => deleteEdge(edge.id)}
            data-testid="inspector-delete-connection-button"
          >
            <Trash2 className="mr-2 h-4 w-4" /> {t('delete_connection')}
          </Button>
        </div>
      </div>
    );
  }

  // ---- Empty state ----
  if (!node) {
    return (
      <div className="flex h-full flex-col bg-[#FBFBFA]" data-testid="inspector-panel">
        <Header title={t('inspector')} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <MousePointer2 className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('no_selection')}</p>
          <p className="text-xs text-muted-foreground">{t('no_selection_hint')}</p>
          <p className="text-xs text-muted-foreground">{t('no_edge_hint')}</p>
        </div>
      </div>
    );
  }

  // ---- Device (node) inspector ----
  const passive = isPassiveDevice(node.data.deviceType);
  const vlan = vlans.find((v) => v.id === node.data.vlanRef);
  const info = vlan && vlan.network ? subnetInfo(vlan.network) : null;
  const ip = node.data.ip || '';
  const ports = node.data.ports || [];
  const portGroups = node.data.portGroups || [];
  const interfaces = node.data.interfaces || [];
  const groupOf = {};
  portGroups.forEach((g) => (g.memberIds || []).forEach((id) => (groupOf[id] = g)));
  const toggleSelectPort = (id) =>
    setSelectedPortIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const panelGroups = ports.length ? getPanelGroups(node) : [];
  let ipState = null;
  if (vlan && vlan.network && ip) {
    ipState = ipInSubnet(ip, vlan.network) ? 'ok' : 'err';
  }
  // IP entry restricted to the variable host part; the fixed part comes from the CIDR.
  const fx = vlan && vlan.network ? ipFixedPrefix(vlan.network) : null;
  const ipHost = fx ? (ip.startsWith(fx.fixed) ? ip.slice(fx.fixed.length) : ip.split('.').slice(fx.octets).join('.')) : ip;
  const onIpHostChange = (raw) => {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    applyData({ ip: cleaned ? fx.fixed + cleaned : '' });
  };
  const onVlanChange = (v) => {
    const newRef = v === 'none' ? null : v;
    const patch = { vlanRef: newRef };
    // Rebase the IP host part onto the newly selected subnet's fixed prefix.
    const nv = vlans.find((x) => x.id === newRef);
    const nfx = nv && nv.network ? ipFixedPrefix(nv.network) : null;
    if (nfx) {
      const host = ip ? ip.split('.').slice(nfx.octets).join('.') : '';
      patch.ip = host ? nfx.fixed + host : '';
    }
    applyData(patch);
  };

  // For an alias placement, find the standard page that owns the master device.
  const masterPageName = isAliasPlacement
    ? pageDisplayName(doc.pages.find((p) => (p.nodes || []).some((n) => n.id === node.id && !n.data?.aliasOf))?.name || '', language)
    : '';

  return (
    <div className="flex h-full flex-col bg-[#FBFBFA]" data-testid="inspector-panel">
      <Header title={t('inspector')} />
      <div className="min-h-0 flex-1 space-y-5 overflow-auto px-4 py-4">
        {isAliasPlacement && (
          <div
            className="flex items-start gap-2.5 rounded-md border border-[#6B4FBB]/30 bg-[#6B4FBB]/[0.06] p-3"
            data-testid="inspector-alias-hint"
          >
            <Copy className="mt-0.5 h-4 w-4 shrink-0 text-[#6B4FBB]" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#4A3690]">{t('alias_inspector_title')}</div>
              <p className="mt-0.5 text-[11px] leading-snug text-[#5b6672]">{t('alias_inspector_body')}</p>
              {masterPageName && (
                <p className="mt-1 text-[11px] text-[#5b6672]">
                  {t('alias_origin_page')}: <span className="font-medium text-[#2A3138]">{masterPageName}</span>
                </p>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-md border border-border bg-white p-3">
          <DeviceIcon type={node.data.deviceType} size={40} />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{node.data.label || deviceLabel(node.data.deviceType, language)}</div>
            <div className="text-xs text-muted-foreground">{deviceLabel(node.data.deviceType, language)}</div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('device_label')}</Label>
          <Input
            data-testid="inspector-device-label-input"
            value={node.data.label}
            onChange={(e) => applyData({ label: e.target.value })}
            placeholder={deviceLabel(node.data.deviceType, language)}
            className="h-9"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('device_description')}</Label>
          <Textarea
            data-testid="inspector-device-description-input"
            value={node.data.description || ''}
            onChange={(e) => applyData({ description: e.target.value })}
            placeholder={t('device_description_ph')}
            rows={2}
            className="min-h-[60px] resize-y text-sm"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('device_type')}</Label>
          <Select value={node.data.deviceType} onValueChange={(v) => applyData({ deviceType: v })}>
            <SelectTrigger className="h-9" data-testid="inspector-device-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEVICE_TYPES.map((d) => (
                <SelectItem key={d.key} value={d.key}>
                  {deviceLabel(d.key, language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {passive && (
          <div
            className="flex items-start gap-2.5 rounded-md border border-[#0B5FFF]/25 bg-[#0B5FFF]/[0.05] p-3"
            data-testid="inspector-passive-hint"
          >
            <Network className="mt-0.5 h-4 w-4 shrink-0 text-[#0B5FFF]" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#0B4FBF]">{t('passive_device_title')}</div>
              <p className="mt-0.5 text-[11px] leading-snug text-[#5b6672]">{t('passive_device_hint')}</p>
            </div>
          </div>
        )}

        {!passive && (
          <>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('vlan')}</Label>
          <Select
            value={node.data.vlanRef || 'none'}
            onValueChange={onVlanChange}
          >
            <SelectTrigger className="h-9" data-testid="inspector-vlan-select">
              <SelectValue placeholder={t('no_vlan')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('no_vlan')}</SelectItem>
              {vlans.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                    VLAN {v.vlanId} · {v.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('ip_address')}</Label>
          {fx ? (
            <div className="flex">
              <span
                className="inline-flex select-none items-center rounded-l-md border border-r-0 border-input bg-muted px-2 font-mono text-[12px] text-muted-foreground"
                data-testid="inspector-ip-prefix"
              >
                {fx.fixed}
              </span>
              <Input
                data-testid="inspector-ip-input"
                value={ipHost}
                onChange={(e) => onIpHostChange(e.target.value)}
                placeholder={t('ip_host_placeholder')}
                className="h-9 rounded-l-none font-mono"
              />
            </div>
          ) : (
            <Input
              data-testid="inspector-ip-input"
              value={ip}
              disabled={!vlan}
              onChange={(e) => applyData({ ip: e.target.value })}
              placeholder={vlan ? t('ip_placeholder') : t('select_vlan_first')}
              className="h-9 font-mono"
            />
          )}
          {ipState === 'ok' && (
            <p className="flex items-center gap-1.5 text-xs text-[#0F7B6C]">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('ip_ok')}
            </p>
          )}
          {ipState === 'err' && (
            <p className="flex items-center gap-1.5 text-xs text-[#B42318]">
              <AlertCircle className="h-3.5 w-3.5" /> {t('ip_invalid')}
            </p>
          )}
          {info && info.valid && (
            <p className="font-mono text-[11px] text-muted-foreground">
              {info.network}/{info.prefix} · {info.firstHost} – {info.lastHost}
            </p>
          )}
        </div>
          </>
        )}

        {/* Ports (defined per device, assigned to connections + detail plan) */}
        <div className="space-y-2.5 rounded-md border border-border bg-white p-3" data-testid="device-ports-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('ports')}</Label>
            <span className="text-[11px] tabular-nums text-muted-foreground">{ports.length}</span>
          </div>

          <div className="flex items-end gap-1.5">
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('port_prefix')}</Label>
              <Input
                value={portPrefix}
                onChange={(e) => setPortPrefix(e.target.value)}
                placeholder="eth"
                className="h-8 font-mono text-sm"
                data-testid="device-port-prefix-input"
              />
            </div>
            <div className="w-16 space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('port_count')}</Label>
              <Input
                type="number"
                min={1}
                max={96}
                value={portCount}
                onChange={(e) => setPortCount(e.target.value)}
                className="h-8 text-center text-sm tabular-nums"
                data-testid="device-port-count-input"
              />
            </div>
            <Button
              size="sm"
              className="h-8 shrink-0 px-2.5"
              onClick={() => addPorts(node.id, portPrefix, portCount)}
              data-testid="device-add-port-button"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> {t('add')}
            </Button>
          </div>

          {ports.length === 0 ? (
            <p className="text-[11px] leading-snug text-muted-foreground">{t('no_ports_device')}</p>
          ) : (
            <div className="space-y-1.5 pt-0.5">
              {ports.map((p) => {
                const grp = groupOf[p.id] || null;
                const grpUntagged = grp ? vlans.find((v) => v.id === grp.untaggedVlanRef) : null;
                const checked = selectedPortIds.includes(p.id);
                return (
                  <div key={p.id} className="flex items-center gap-1.5">
                    {!passive && (
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSelectPort(p.id)}
                        className="h-4 w-4 shrink-0"
                        aria-label={t('select_port')}
                        data-testid={`port-select-${p.id}`}
                      />
                    )}
                    <Input
                      value={p.name}
                      onChange={(e) => updatePort(node.id, p.id, { name: e.target.value })}
                      className="h-8 font-mono text-sm"
                      data-testid={`device-port-input-${p.id}`}
                    />
                    {!passive && (grp ? (
                      <span
                        className="flex h-8 shrink-0 items-center gap-1 rounded-md border border-border bg-muted/60 px-1.5 text-[10px] font-medium text-muted-foreground"
                        title={`${t('in_group')}: ${grp.name || t('group')}`}
                        data-testid={`port-group-chip-${p.id}`}
                      >
                        <Tags className="h-3 w-3" />
                        {grpUntagged && <span className="h-2.5 w-2.5 rounded-full" style={{ background: grpUntagged.color }} />}
                        <span className="max-w-[68px] truncate">{grp.name || t('group')}</span>
                      </span>
                    ) : (
                      <PortVlanPopover
                        node={node}
                        port={p}
                        vlans={vlans}
                        t={t}
                        applyPort={(portId, patch) => updatePort(node.id, portId, patch)}
                        withIp
                      />
                    ))}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-[#B42318]"
                      onClick={() => deletePort(node.id, p.id)}
                      aria-label={t('delete')}
                      data-testid={`device-port-delete-${p.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selection action bar (create / add-to group) */}
          {!passive && selectedPortIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-[#0B5FFF]/30 bg-[#0B5FFF]/5 p-2" data-testid="port-selection-bar">
              <span className="text-[11px] font-medium text-[#0B5FFF]" data-testid="port-selection-count">
                {t('selected_count', { n: selectedPortIds.length })}
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <Button
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    createPortGroup(node.id, selectedPortIds, `${t('group')} ${portGroups.length + 1}`);
                    setSelectedPortIds([]);
                  }}
                  data-testid="create-group-button"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t('create_group')}
                </Button>
                {portGroups.length > 0 && (
                  <Select
                    onValueChange={(gid) => {
                      addPortsToGroup(node.id, gid, selectedPortIds);
                      setSelectedPortIds([]);
                    }}
                  >
                    <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="add-to-group-select">
                      <SelectValue placeholder={t('add_to_group')} />
                    </SelectTrigger>
                    <SelectContent>
                      {portGroups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name || t('group')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedPortIds([])} data-testid="clear-selection-button">
                  {t('clear_selection')}
                </Button>
              </div>
            </div>
          )}

          <p className="text-[11px] leading-snug text-muted-foreground">{t('ports_hint')}</p>
        </div>

        {/* Port config groups (tagged/untagged set once per group) */}
        {!passive && portGroups.length > 0 && (
          <div className="space-y-2.5 rounded-md border border-border bg-white p-3" data-testid="port-groups-section">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <Tags className="h-3.5 w-3.5" /> {t('port_groups')}
              </Label>
              <span className="text-[11px] tabular-nums text-muted-foreground">{portGroups.length}</span>
            </div>
            <div className="space-y-2">
              {portGroups.map((g) => {
                const gUntagged = vlans.find((v) => v.id === g.untaggedVlanRef);
                return (
                  <div key={g.id} className="space-y-1.5 rounded-md border border-border bg-[#FBFBFA] p-2" data-testid={`port-group-${g.id}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-3 w-3 shrink-0 rounded-full border" style={{ background: gUntagged ? gUntagged.color : 'transparent', borderColor: gUntagged ? gUntagged.color : '#cbd5e1' }} />
                      <Input
                        value={g.name}
                        onChange={(e) => updatePortGroup(node.id, g.id, { name: e.target.value })}
                        placeholder={t('group_name')}
                        className="h-8 text-sm"
                        data-testid={`port-group-name-${g.id}`}
                      />
                      <PortVlanPopover
                        node={node}
                        port={g}
                        vlans={vlans}
                        t={t}
                        applyPort={(gid, patch) => updatePortGroup(node.id, g.id, patch)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-[#B42318]"
                        onClick={() => deletePortGroup(node.id, g.id)}
                        aria-label={t('delete_group')}
                        data-testid={`port-group-delete-${g.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(g.memberIds || []).map((mid) => {
                        const mp = ports.find((pp) => pp.id === mid);
                        if (!mp) return null;
                        return (
                          <span key={mid} className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-[#2A3138] ring-1 ring-border" data-testid={`group-member-${g.id}-${mid}`}>
                            {mp.name}
                            <button
                              type="button"
                              onClick={() => removePortFromGroup(node.id, mid)}
                              className="text-muted-foreground hover:text-[#B42318]"
                              aria-label={t('remove_from_group')}
                              data-testid={`group-member-remove-${mid}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('port_groups_hint')}</p>
          </div>
        )}

        {/* Virtual interfaces (SVI / VLAN-IF) — own VLAN config + IP */}
        {!passive && (
        <div className="space-y-2.5 rounded-md border border-border bg-white p-3" data-testid="interfaces-section">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
              <Network className="h-3.5 w-3.5" /> {t('interfaces')}
            </Label>
            <span className="text-[11px] tabular-nums text-muted-foreground">{interfaces.length}</span>
          </div>
          {interfaces.length === 0 ? (
            <p className="text-[11px] leading-snug text-muted-foreground">{t('no_interfaces')}</p>
          ) : (
            <div className="space-y-1.5">
              {interfaces.map((it) => (
                <div key={it.id} className="space-y-1.5">
                <div className="flex items-center gap-1.5" data-testid={`interface-row-${it.id}`}>
                  <Input
                    value={it.name || ''}
                    onChange={(e) => updateInterface(node.id, it.id, { name: e.target.value })}
                    placeholder={t('interface_name')}
                    className="h-8 w-[110px] shrink-0 font-mono text-sm"
                    data-testid={`interface-name-${it.id}`}
                  />
                  {(() => {
                    const iuVlan = vlans.find((v) => v.id === it.untaggedVlanRef);
                    return (
                      <HostIpInput
                        cidr={iuVlan?.network}
                        value={it.ip || ''}
                        onChange={(val) => updateInterface(node.id, it.id, { ip: val })}
                        placeholder={iuVlan ? t('ip_host_placeholder') : t('ip_placeholder')}
                        className="flex-1"
                        testid={`interface-ip-${it.id}`}
                      />
                    );
                  })()}
                  <PortVlanPopover
                    node={node}
                    port={it}
                    vlans={vlans}
                    t={t}
                    applyPort={(id, patch) => updateInterface(node.id, it.id, patch)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-[#B42318]"
                    onClick={() => deleteInterface(node.id, it.id)}
                    aria-label={t('delete_interface')}
                    data-testid={`interface-delete-${it.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {(it.taggedVlanRefs || []).length > 0 && (
                  <div className="ml-1.5 space-y-1 border-l-2 border-border/60 pl-2.5" data-testid={`interface-tagged-ips-${it.id}`}>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('iface_tagged_ips')}</Label>
                    {(it.taggedVlanRefs || []).map((vref) => {
                      const tv = vlans.find((v) => v.id === vref);
                      if (!tv) return null;
                      return (
                        <div key={vref} className="flex items-center gap-1.5">
                          <span className="flex w-[110px] shrink-0 items-center gap-1.5">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: tv.color }} />
                            <span className="truncate text-[11px] text-muted-foreground">VLAN {tv.vlanId} · {tv.name}</span>
                          </span>
                          <HostIpInput
                            cidr={tv.network}
                            value={(it.taggedIps || {})[vref] || ''}
                            onChange={(val) => {
                              const next = { ...(it.taggedIps || {}) };
                              if ((val || '').trim()) next[vref] = val;
                              else delete next[vref];
                              updateInterface(node.id, it.id, { taggedIps: next });
                            }}
                            placeholder={tv.network ? t('ip_host_placeholder') : t('ip_placeholder')}
                            className="flex-1"
                            testid={`interface-tagged-ip-${it.id}-${vref}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              ))}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-full text-xs"
            onClick={() => addInterface(node.id)}
            data-testid="add-interface-button"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> {t('add_interface')}
          </Button>
          <p className="text-[11px] leading-snug text-muted-foreground">{t('interfaces_hint')}</p>
        </div>
        )}

        {/* Panel layout (front-panel port grouping for detail Panel view) */}
        {ports.length > 0 && (
          <PanelLayoutSection groups={panelGroups} onChange={(panel) => applyData({ panel })} t={t} />
        )}

        {isDetail ? (
          <>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('detail_device_info')}</p>
            <Button
              variant="outline"
              className="w-full border-[#B42318]/30 text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
              onClick={() => removeDetailMember(page.id, node.id)}
              data-testid="inspector-remove-from-detail-button"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t('remove_from_detail')}
            </Button>
          </>
        ) : isLocation ? (
          <>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('detail_device_info')}</p>
            <Button
              variant="outline"
              className="w-full border-[#B42318]/30 text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
              onClick={() => removeLocationMarker(page.id, node.id)}
              data-testid="inspector-remove-from-location-button"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t('remove_from_location')}
            </Button>
          </>
        ) : isAliasPlacement ? (
          <Button
            variant="outline"
            className="w-full border-[#B42318]/30 text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
            onClick={() => deleteNode(rawNode.id)}
            data-testid="inspector-remove-alias-button"
          >
            <Trash2 className="mr-2 h-4 w-4" /> {t('alias_remove_placement')}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="w-full border-[#B42318]/30 text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
            onClick={() => deleteNode(node.id)}
            data-testid="inspector-delete-device-button"
          >
            <Trash2 className="mr-2 h-4 w-4" /> {t('delete_device')}
          </Button>
        )}
      </div>
    </div>
  );
};
